/* eslint-disable @typescript-eslint/no-empty-function */
export interface HostAdapter {
  kind: "telegram" | "local";
  initData: () => string;
  colorScheme: () => "light" | "dark";
  onThemeChange: (listener: (mode: "light" | "dark") => void) => () => void;
  /** BCP-47-ish tag from the host, e.g. "ru" or "en-US". */
  languageCode: () => string;
  ready: () => void;
  expand: () => void;
  close: () => void;
  print: () => void;
  /** True when the surface can actually paint a print preview. */
  canPrint: () => boolean;
  openExternal: (url: string) => void;
  /**
   * Open a Telegram invoice over the running app. The payment sheet slides
   * on top, this page stays alive, and the callback reports what the person
   * did — never whether we may grant anything, which only the backend says.
   */
  openInvoice: (url: string, onDone: (status: string) => void) => void;
  /** Semantic surface.telegram.header — kept equal to surface.canvas. */
  setHeaderColor: (color: string) => void;
  /** Semantic surface.telegram.bottomBar — kept equal to surface.raised. */
  setBottomBarColor: (color: string) => void;
}
/** Only absolute https: URLs may leave the app. */
export function isHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}
const localAdapter: HostAdapter = {
  kind: "local",
  initData: () => "",
  colorScheme: () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  onThemeChange: (listener) => {
    if (typeof window.matchMedia !== "function") return () => {};
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      listener(query.matches ? "dark" : "light");
    };
    query.addEventListener("change", handler);
    return () => {
      query.removeEventListener("change", handler);
    };
  },
  languageCode: () => navigator.language,
  ready: () => undefined,
  expand: () => undefined,
  close: () => undefined,
  print: () => {
    window.print();
  },
  canPrint: () => typeof window.print === "function",
  openExternal: (url) => {
    if (isHttpsUrl(url)) window.open(url, "_blank", "noopener,noreferrer");
  },
  // No Telegram outside Telegram: nothing to pay with, so nothing opens.
  openInvoice: (_url, onDone) => {
    onDone("unsupported");
  },
  setHeaderColor: () => undefined,
  setBottomBarColor: () => undefined,
};
export function createHostAdapter(): HostAdapter {
  const telegram = (
    window as Window & {
      Telegram?: {
        WebApp?: {
          initData?: string;
          initDataUnsafe?: { user?: { language_code?: string } };
          colorScheme?: "light" | "dark";
          ready?: () => void;
          expand?: () => void;
          close?: () => void;
          onEvent?: (event: string, callback: () => void) => void;
          offEvent?: (event: string, callback: () => void) => void;
          openLink?: (url: string) => void;
          openInvoice?: (
            url: string,
            callback: (status: string) => void,
          ) => void;
          setHeaderColor?: (color: string) => void;
          setBottomBarColor?: (color: string) => void;
        };
      };
    }
  ).Telegram?.WebApp;
  if (!telegram?.initData) return localAdapter;
  return {
    kind: "telegram",
    initData: () => telegram.initData ?? "",
    colorScheme: () => telegram.colorScheme ?? "light",
    onThemeChange: (listener) => {
      const callback = () => {
        listener(telegram.colorScheme ?? "light");
      };
      telegram.onEvent?.("themeChanged", callback);
      return () => telegram.offEvent?.("themeChanged", callback);
    },
    languageCode: () =>
      telegram.initDataUnsafe?.user?.language_code ?? navigator.language,
    ready: () => telegram.ready?.(),
    expand: () => telegram.expand?.(),
    close: () => telegram.close?.(),
    print: () => {
      window.print();
    },
    // Some Telegram WebViews expose no print pipeline at all; the Print Form
    // screen offers "open in browser" instead of a dead button.
    canPrint: () => typeof window.print === "function",
    openExternal: (url) => {
      if (isHttpsUrl(url)) telegram.openLink?.(url);
    },
    openInvoice: (url, onDone) => {
      if (telegram.openInvoice === undefined) {
        // An older Telegram client. Falling back to a link keeps the
        // purchase possible instead of leaving a dead button.
        telegram.openLink?.(url);
        onDone("unsupported");
        return;
      }
      telegram.openInvoice(url, onDone);
    },
    setHeaderColor: (color) => telegram.setHeaderColor?.(color),
    setBottomBarColor: (color) => telegram.setBottomBarColor?.(color),
  };
}
